import { ReactNode } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useSubscriptionAccess, FeatureLimits } from '@/hooks/useSubscriptionAccess';
import { useSubscription } from '@/hooks/useSubscription';
import { Lock, Zap, Crown } from 'lucide-react';

interface PremiumFeatureGateProps {
  feature: keyof FeatureLimits;
  children: ReactNode;
  fallback?: ReactNode;
  showUpgradeCard?: boolean;
}

export function PremiumFeatureGate({ 
  feature, 
  children, 
  fallback,
  showUpgradeCard = true 
}: PremiumFeatureGateProps) {
  const { isFeatureAvailable, getUpgradeMessage } = useSubscriptionAccess();
  const { createCheckout } = useSubscription();

  if (isFeatureAvailable(feature)) {
    return <>{children}</>;
  }

  if (fallback) {
    return <>{fallback}</>;
  }

  if (!showUpgradeCard) {
    return null;
  }

  const handleUpgrade = (plan: string) => {
    createCheckout(plan);
  };

  return (
    <Card className="border-2 border-dashed border-muted-foreground/25">
      <CardHeader className="text-center">
        <div className="mx-auto w-12 h-12 bg-gradient-to-r from-primary to-primary-glow rounded-full flex items-center justify-center mb-4">
          <Lock className="h-6 w-6 text-white" />
        </div>
        <CardTitle className="flex items-center justify-center gap-2">
          Premium Feature
          <Badge variant="secondary">
            <Crown className="h-3 w-3 mr-1" />
            Upgrade erforderlich
          </Badge>
        </CardTitle>
        <CardDescription>
          {getUpgradeMessage(feature)}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid gap-2">
          {feature === 'hasAnalytics' && (
            <Button 
              onClick={() => handleUpgrade('basic')} 
              className="w-full"
            >
              <Zap className="h-4 w-4 mr-2" />
              Upgrade auf Basic Plan (29€/Monat)
            </Button>
          )}
          {(feature === 'hasAdvancedCRM' || feature === 'hasLandingPages' || feature === 'hasWalletIntegration') && (
            <>
              <Button 
                onClick={() => handleUpgrade('pro')} 
                className="w-full"
              >
                <Zap className="h-4 w-4 mr-2" />
                Upgrade auf Pro Plan (79€/Monat)
              </Button>
              <Button 
                onClick={() => handleUpgrade('basic')} 
                variant="outline"
                className="w-full"
              >
                Basic Plan (29€/Monat) - Grundfunktionen
              </Button>
            </>
          )}
          {(feature === 'hasCustomBranding' || feature === 'hasPrioritySupport') && (
            <>
              <Button 
                onClick={() => handleUpgrade('enterprise')} 
                className="w-full bg-gradient-to-r from-purple-600 to-purple-800 hover:from-purple-700 hover:to-purple-900"
              >
                <Crown className="h-4 w-4 mr-2" />
                Upgrade auf Enterprise Plan (199€/Monat)
              </Button>
              <Button 
                onClick={() => handleUpgrade('pro')} 
                variant="outline"
                className="w-full"
              >
                Pro Plan (79€/Monat) - Erweiterte Funktionen
              </Button>
            </>
          )}
        </div>
        
        <div className="text-center pt-2">
          <p className="text-xs text-muted-foreground">
            Alle Pläne mit 14 Tagen kostenloser Testphase
          </p>
        </div>
      </CardContent>
    </Card>
  );
}